/**
 * A language in the website's header (the one you know, or the one you're
 * learning), and its picker: a popover under it, or a sheet on phones. The
 * iOS app's header and picker are native (nativeHeader.ts).
 */
import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as modalController from "modalController";
import * as buttonRole from "ui/buttonRole";
import Popover from "ui/Popover";
import Sheet, { showSheet } from "ui/Sheet";
import LanguageSelectorView from "views/LanguageSelectorView";

interface Props {
  /** Its button's */
  id: string;
  /** The language's name, on its button */
  label: string;
  /** The picker's, e.g. on its sheet */
  title: string;
  languageType: "first" | "learning";
  currentLanguage: string;
  featuredLanguages?: string[];
  onSelect: (languageCode: string) => void;
}

interface State {
  open: boolean;
}

export default class HeaderLanguagePicker extends preact.Component<
  Props,
  State
> {
  state: State = { open: false };
  private disposeViewportWatch?: () => void;

  componentDidMount() {
    // On phones it's a sheet, so a popover left open from a wider window shuts
    this.disposeViewportWatch = effect(() => {
      if (deviceDetect.smallViewport.value) this.close();
    });
  }

  componentWillUnmount() {
    this.disposeViewportWatch?.();
  }

  private close = () => this.setState({ open: false });

  private toggle = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (deviceDetect.smallViewport.value) {
      showSheet(
        <Sheet title={this.props.title}>
          {this.renderSelector(() => modalController.cancel())}
        </Sheet>
      );
    } else {
      this.setState({ open: !this.state.open });
    }
  };

  /** The languages, closing it as one's picked */
  private renderSelector(close: () => void) {
    return (
      <LanguageSelectorView
        languageType={this.props.languageType}
        featuredLanguages={this.props.featuredLanguages}
        currentLanguage={this.props.currentLanguage}
        setLanguage={(languageCode) => {
          close();
          this.props.onSelect(languageCode);
        }}
      />
    );
  }

  render() {
    const { open } = this.state;
    return (
      <Popover
        class="dropdownBox languagePicker"
        popoverClass="languagePopover"
        role="dialog"
        aria-label={this.props.title}
        align="start"
        open={open}
        onClose={this.close}
        anchor={
          <span
            {...buttonRole.props}
            id={this.props.id}
            class="dropdownTitle"
            aria-expanded={open}
            onClick={this.toggle}
          >
            <span>{this.props.label}</span>
            <span class="dropdownArrow"></span>
          </span>
        }
      >
        {this.renderSelector(this.close)}
      </Popover>
    );
  }
}
